// RuleGroup.jsx — recursive AND/OR group of rules + nested groups
//
// Visual style: neutral, matches the dashboard's existing design system.
//
// Important: AVOID `style={{ gap: ... }}` on .d-flex.align-items-center —
// the project has a global CSS rule in enhanced-heading.css that injects a
// border into any such element. Use Bootstrap margin utilities instead.

import React, { useRef } from 'react';
import { Button, ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import { Plus, X } from 'lucide-react';
import RuleRow from './RuleRow.jsx';

const EMPTY_RULE = { field: null, op: null, value: undefined };
const EMPTY_GROUP = () => ({ match: 'all', rules: [{ ...EMPTY_RULE }] });

let _idCounter = 0;
const nextId = () => `rg-${++_idCounter}-${Math.random().toString(36).slice(2, 7)}`;

const RuleGroup = ({ group, onChange, onRemove, depth = 0 }) => {
  const tooltipIdRef = useRef(nextId());

  const setMatch = (m) => onChange({ ...group, match: m });
  const updateChild = (idx, child) => {
    const next = [...group.rules]; next[idx] = child;
    onChange({ ...group, rules: next });
  };
  const removeChild = (idx) => onChange({ ...group, rules: group.rules.filter((_, i) => i !== idx) });
  const addRule = () => onChange({ ...group, rules: [...group.rules, { ...EMPTY_RULE }] });
  const addGroup = () => onChange({ ...group, rules: [...group.rules, EMPTY_GROUP()] });

  const isAny = group.match === 'any';
  const connectorLabel = isAny ? 'OR' : 'AND';
  const rules = group.rules || [];
  const isEmpty = rules.length === 0;

  // Nested groups keep a subtle border + tint so they stand out from sibling rules.
  // Top-level group: no wrapper at all (parent Card provides the container).
  const containerStyle = depth === 0
    ? null
    : {
        border: '1px solid #d7d7d7',
        borderRadius: 4,
        padding: 16,
        marginBottom: 12,
        background: '#fafafa',
      };

  return (
    <div style={containerStyle}>
      {/* Match toggle row — uses margin utilities (no inline gap) */}
      <div className="mb-3">
        <span className="mr-2" style={{ fontSize: '0.875rem', color: '#303030' }}>Match</span>
        <ButtonGroup size="sm" className="mr-2">
          <Button color="primary" outline={isAny} onClick={() => setMatch('all')}>ALL of</Button>
          <Button color="primary" outline={!isAny} onClick={() => setMatch('any')}>ANY of</Button>
        </ButtonGroup>
        <span style={{ fontSize: '0.875rem', color: '#8f8f8f' }}>the following rules</span>
        {depth > 0 && (
          <Button
            color="link" size="sm"
            onClick={onRemove}
            className="p-0 float-right"
            style={{ color: '#8f8f8f' }}
            title="Remove nested group"
          >
            <X size={16} />
          </Button>
        )}
      </div>

      {isEmpty && (
        <div style={{ padding: '12px 0', fontSize: '0.875rem', color: '#8f8f8f' }}>
          No rules yet.
        </div>
      )}

      {rules.map((child, idx) => (
        <div key={idx}>
          {idx > 0 && (
            <div
              className="d-flex"
              style={{ margin: '10px 0', alignItems: 'center' }}
            >
              <span style={{ flex: 1, height: 1, background: '#e6e6e6' }} />
              <span
                style={{
                  display: 'inline-block',
                  margin: '0 12px',
                  padding: '2px 12px',
                  border: '1px solid #3498db',
                  borderRadius: 12,
                  background: '#eaf4fc',
                  color: '#3498db',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  minWidth: 48,
                  textAlign: 'center',
                }}
              >
                {connectorLabel}
              </span>
              <span style={{ flex: 1, height: 1, background: '#e6e6e6' }} />
            </div>
          )}
          {child.rules ? (
            <RuleGroup
              group={child}
              onChange={(g) => updateChild(idx, g)}
              onRemove={() => removeChild(idx)}
              depth={depth + 1}
            />
          ) : (
            <RuleRow
              rule={child}
              onChange={(r) => updateChild(idx, r)}
              onRemove={() => removeChild(idx)}
            />
          )}
        </div>
      ))}

      {/* Add actions — link-style, no inline gap */}
      <div style={{ marginTop: rules.length > 0 ? 12 : 0 }}>
        <Button
          color="link" size="sm" onClick={addRule}
          className="p-0 mr-3"
          style={{ color: '#3498db', fontSize: '0.875rem' }}
        >
          <Plus size={14} style={{ verticalAlign: 'text-bottom', marginRight: 2 }} /> Add rule
        </Button>
        <Button
          id={tooltipIdRef.current}
          color="link" size="sm" onClick={addGroup}
          className="p-0"
          style={{ color: '#3498db', fontSize: '0.875rem' }}
        >
          <Plus size={14} style={{ verticalAlign: 'text-bottom', marginRight: 2 }} /> Add group
        </Button>
        <UncontrolledTooltip placement="top" target={tooltipIdRef.current} delay={{ show: 400, hide: 0 }}>
          Nest rules with different logic — e.g. <strong>(Dubai OR Abu Dhabi)</strong> inside an AND group.
        </UncontrolledTooltip>
      </div>
    </div>
  );
};

export default RuleGroup;
